/**
 * AI CONTEXT: mobile-first - Marketing landing page with giveaways teaser
 * Journal: J-20260108-01
 * 
 * This page implements the home experience with:
 * - Brand hero, feature highlights, and CTAs
 * - Featured products and active giveaways preview
 * - Consistent dark theme and glassmorphism styling
 * - Mobile-first responsive layout
 * 
 * Architecture decisions:
 * - Use `TimeDisplay` for real-time UI accent
 * - Load giveaways from `GiveawayContext`
 * - Use `getAssetUrl` with robust image fallbacks
 * 
 * Future considerations:
 * - Add A/B testing for hero/CTA variants
 * - Lazy-load sections for performance
 * - Add analytics events for CTA clicks
 */

import React, { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Gift, Star, ShoppingBag } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useGiveaway } from '../contexts/GiveawayContext';
import { useCart } from '../contexts/CartContext';
import { useProducts } from '../contexts/ProductContext';
import { useSubscription } from '../contexts/SubscriptionContext';
import { setSEO, injectJsonLd, absoluteUrl } from '../utils/seo';
// import TimeDisplay from '../components/TimeDisplay';
import S3Image from '../components/S3Image';
import CountdownTimer from '../components/CountdownTimer';
import ClassifiedPrizeMedia from '../components/ClassifiedPrizeMedia';
import { t } from '../i18n';
import { ensureDate, normalizeDateForVisibility, shouldShowClassifiedPrize } from '../utils/date';
import { compareGiveawaysByRecency } from '../utils/giveawaySort';

// Gallery component for consistent image handling with Giveaways.js
const CardImageGallery = React.memo(({ items, alt }) => {
  const [active, setActive] = React.useState(0);
  const safe = Array.isArray(items) && items.length > 0 ? items : ['/files/Logo.png'];
  const main = safe[Math.min(active, safe.length - 1)];
  const startXRef = React.useRef(0);
  const pointerDownRef = React.useRef(false);
  const onPrev = () => setActive(i => Math.max(0, i - 1));
  const onNext = () => setActive(i => Math.min(safe.length - 1, i + 1));

  return (
    <div
      className="relative overflow-hidden"
      onTouchStart={(e) => { if (e.touches && e.touches[0]) startXRef.current = e.touches[0].clientX; }}
      onTouchEnd={(e) => {
        const t = e.changedTouches && e.changedTouches[0];
        if (!t) return;
        const dx = t.clientX - startXRef.current;
        if (Math.abs(dx) > 30) {
          if (dx < 0) onNext(); else onPrev();
        }
      }}
      onPointerDown={(e) => { pointerDownRef.current = true; startXRef.current = e.clientX; }}
      onPointerUp={(e) => {
        if (!pointerDownRef.current) return;
        pointerDownRef.current = false;
        const dx = e.clientX - startXRef.current;
        if (Math.abs(dx) > 30) {
          if (dx < 0) onNext(); else onPrev();
        }
      }}
    >
      <S3Image
        s3KeyOrUrl={main}
        alt={alt}
        className="w-full h-48 max-w-full max-h-full object-contain p-4 rounded-t-lg"
        fallback="/files/Logo.png"
      />
      {safe.length > 1 && (
        <>
          <button
            type="button"
            aria-label="Previous image"
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); onPrev(); }}
            className="flex absolute left-2 top-1/2 -translate-y-1/2 bg-theme-black/50 hover:bg-theme-black/70 text-theme-silver rounded-full w-8 h-8 items-center justify-center z-10"
          >
            ‹
          </button>
          <button
            type="button"
            aria-label="Next image"
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); onNext(); }}
            className="flex absolute right-2 top-1/2 -translate-y-1/2 bg-theme-black/50 hover:bg-theme-black/70 text-theme-silver rounded-full w-8 h-8 items-center justify-center z-10"
          >
            ›
          </button>
          <div className="absolute bottom-2 left-0 right-0 px-2">
            <div
              className="flex gap-2 overflow-x-auto whitespace-nowrap justify-start mx-auto max-w-full scrollbar-thin scrollbar-thumb-theme-silver/30 scrollbar-track-transparent"
              style={{ WebkitOverflowScrolling: 'touch', overscrollBehaviorX: 'contain' }}
            >
              {safe.map((thumb, idx) => (
                <button
                  key={thumb + idx}
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); setActive(idx); }}
                  className={`border rounded ${active === idx ? 'border-custom-orange' : 'border-theme-silver/30'} bg-theme-black/60 flex-shrink-0`}
                  title={`Image ${idx + 1}`}
                >
                  <S3Image s3KeyOrUrl={thumb} alt="thumb" className="w-10 h-10 object-contain" fallback="/files/Logo.png" />
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
});

const Home = () => {
  const { isAuthenticated, user } = useAuth();
  const { giveaways, fetchGiveaways } = useGiveaway();
  const { addToCart } = useCart();
  const { products } = useProducts();
  const { isUserSubscribed: subscriptionIsUserSubscribed } = useSubscription();
  const [currentTime, setCurrentTime] = useState(new Date());
  const [prompt, setPrompt] = useState(null);

  const isSubscriber = useMemo(() => {
    if (!user) return false;

    if (typeof subscriptionIsUserSubscribed === 'function') {
      try {
        const derived = subscriptionIsUserSubscribed(user.id || user.email);
        if (typeof derived === 'boolean') {
          return derived;
        }
      } catch (subscriptionError) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('🔍 Home: SubscriptionContext.isUserSubscribed threw an error:', subscriptionError);
        }
      }
    }

    return Boolean(user?.isSubscriber);
  }, [subscriptionIsUserSubscribed, user]);

  // Use up to 3 products from API (or demo via context)
  // const featuredProducts = useMemo(() => products.slice(0, 3), [products]);

  // AI CONTEXT: business-logic - 7-day grace period for sold out/ended giveaways (matches Giveaways.js)
  // Reasoning: Giveaways that sell out or reach end date stay in active tab for 7 days marked as sold out
  // Constraints: After 7 days, they move to past tab. No new entries allowed when sold out or end date reached.
  // Journal: J-20251222-01
  const isExpired = (g) => {
    if (!g) return false;

    const isSoldOut = (Number(g?.availableSlots) || 0) === 0;
    const hasWinnerOrCompleted = g?.status === 'completed' || !!g?.winner;
    const endDate = g?.endDate ? ensureDate(g.endDate) : null;

    // If there is no end date:
    // - Completed/winner giveaways are immediately past
    // - Sold out giveaways without an end date stay active until manually archived
    if (!endDate) {
      if (hasWinnerOrCompleted) return true;
      return false;
    }

    const endDateReached = endDate && endDate < currentTime;

    // If neither sold out, completed/winner, nor past end date, it's still active
    if (!isSoldOut && !hasWinnerOrCompleted && !endDateReached) return false;

    // For sold out or completed/winner or endDateReached giveaways,
    // keep them in the active section for 7 days after the end date, then move to past.
    const sevenDaysAfterEnd = new Date(endDate);
    sevenDaysAfterEnd.setDate(sevenDaysAfterEnd.getDate() + 7);

    return currentTime >= sevenDaysAfterEnd;
  };
  
  const activeGiveawaysAll = giveaways.filter(g => !isExpired(g));

  const upcomingGiveawaysAll = giveaways.filter(g => {
    const now = new Date(currentTime);
    const startDate = normalizeDateForVisibility(g.startDate);
    const isSubscriptionOnly = Boolean(g.subscriptionVisibilityOnly);
    return !isSubscriptionOnly && startDate && Number.isFinite(startDate.getTime()) && now < startDate;
  }).sort((a, b) => {
    const aDate = normalizeDateForVisibility(a.startDate);
    const bDate = normalizeDateForVisibility(b.startDate);
    if (!aDate || !bDate) return 0;
    return aDate - bDate;
  });

  const upcomingFallback = upcomingGiveawaysAll;

  const activeGiveaways = (activeGiveawaysAll.length > 0 ? activeGiveawaysAll : upcomingFallback)
    .sort((a, b) => {
      const aAvailable = Number(a.availableSlots) || 0;
      const bAvailable = Number(b.availableSlots) || 0;
      const aSoldOut = aAvailable === 0;
      const bSoldOut = bAvailable === 0;

      if (aSoldOut !== bSoldOut) {
        return aSoldOut ? 1 : -1;
      }

      return compareGiveawaysByRecency(a, b);
    })
    .slice(0, 2);

  /*
   * Hero instrumentation. The competitor's tagline claims "fair odds" but no
   * number ever appears on their site; printing real figures above the fold is
   * the differentiator. Every value here is derived from live giveaway data —
   * nothing is hard-coded — and any stat that can't be computed is dropped
   * rather than shown as a zero.
   */
  const heroStats = useMemo(() => {
    const live = activeGiveawaysAll.filter(g => (Number(g?.availableSlots) || 0) > 0);

    const slotCounts = live
      .map(g => Number(g?.totalSlots))
      .filter(n => Number.isFinite(n) && n > 0);
    const bestOdds = slotCounts.length ? Math.min(...slotCounts) : null;

    const openSlots = live.reduce((sum, g) => sum + (Number(g?.availableSlots) || 0), 0);

    const stats = [];
    if (live.length > 0) {
      stats.push({ value: String(live.length).padStart(2, '0'), label: 'Live Drawings' });
    }
    if (bestOdds) {
      stats.push({ value: `1:${bestOdds}`, label: 'Best Odds' });
    }
    if (openSlots > 0) {
      stats.push({ value: openSlots.toLocaleString(), label: 'Slots Open' });
    }
    return stats;
  }, [activeGiveawaysAll]);

  // Debug: Log when giveaways change
  useEffect(() => {
    console.log('🔍 Home: Giveaways updated, count:', giveaways.length);
    console.log('🔍 Home: Active giveaways (all):', activeGiveawaysAll.length);
    console.log('🔍 Home: Active giveaways (displayed):', activeGiveaways.length);
    console.log('🔍 Home: User authenticated:', isAuthenticated, 'isAdmin:', user?.isAdmin, 'isSubscriber:', isSubscriber);
    console.log('🔍 Home: All giveaway titles:', giveaways.map(g => g.title));
    console.log('🔍 Home: Displayed giveaway titles:', activeGiveaways.map(g => g.title));
    console.log('🔍 Home: Full user object:', user);
  }, [giveaways, activeGiveaways, activeGiveawaysAll.length, isAuthenticated, user, isSubscriber]);

  // Real-time clock for countdown timers
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // Listen for giveaway data updates from admin panel
  useEffect(() => {
    const handleGiveawayUpdate = () => {
      console.log('🔍 Home: Received giveaway update event, refreshing...');
      console.log('🔍 Home: Current giveaways count before refresh:', giveaways.length);
      if (fetchGiveaways) {
        fetchGiveaways().then(() => {
          console.log('🔍 Home: fetchGiveaways completed');
        }).catch(error => {
          console.error('🔍 Home: fetchGiveaways failed:', error);
        });
      } else {
        console.warn('🔍 Home: fetchGiveaways is not available');
      }
    };

    console.log('🔍 Home: Setting up giveawayDataUpdated event listener');
    window.addEventListener('giveawayDataUpdated', handleGiveawayUpdate);
    return () => {
      console.log('🔍 Home: Removing giveawayDataUpdated event listener');
      window.removeEventListener('giveawayDataUpdated', handleGiveawayUpdate);
    };
  }, [fetchGiveaways, giveaways.length]);


  useEffect(() => {
    setSEO({
      title: 'Tolbert Tactical — Premium Gear & Exclusive Giveaways',
      description: 'Shop premium tactical gear and earn entries to exclusive giveaways. Join now for early access and monthly credits.',
      path: '/',
      image: '/files/Logo.png'
    });

    const org = {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: 'Tolbert Tactical',
      url: 'https://tolberttactical.com/',
      logo: absoluteUrl('/files/Logo.png'),
      sameAs: []
    };
    injectJsonLd('org-jsonld', org);

    const website = {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      name: 'Tolbert Tactical',
      url: 'https://tolberttactical.com/',
      potentialAction: {
        '@type': 'SearchAction',
        target: 'https://tolberttactical.com/store?search={search_term_string}',
        'query-input': 'required name=search_term_string'
      }
    };
    injectJsonLd('website-jsonld', website);
  }, []);

  return (
    <div className="min-h-screen bg-theme-black">
      {/* Hero Section with Time Display */}
      {/*
        The background photo is brass-colored cartridges, which is the same hue
        as the brand accent — at the previous 60% overlay the copper headline
        vanished into it. It is now desaturated and darkened to near-black so it
        reads as texture, letting the type and the stat strip carry the fold.
      */}
      <section className="relative min-h-[78vh] flex items-center py-16 overflow-hidden">
        <div
          aria-hidden="true"
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `url(${process.env.PUBLIC_URL}/files/background.png)`,
            filter: 'grayscale(0.55) brightness(0.28) contrast(1.1)',
          }}
        />
        <div aria-hidden="true" className="absolute inset-0 bg-theme-carbon/70" />
        <div
          aria-hidden="true"
          className="absolute inset-x-0 bottom-0 h-40"
          style={{ background: 'linear-gradient(to bottom, rgba(11,11,12,0), #0B0B0C)' }}
        />

        <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <S3Image
              s3KeyOrUrl="/files/Logo.png"
              alt="Tolbert Tactical"
              className="h-24 w-auto object-contain mb-8"
              fallback="/files/Logo.png"
            />

            <h1 className="font-display tracking-display text-5xl md:text-7xl font-extrabold uppercase leading-[0.95] mb-5">
              {t('home.premiumGear')}
              <span className="block text-custom-orange">{t('home.exclusiveGiveaways')}</span>
            </h1>

            <p className="text-lg md:text-xl text-theme-silver-light max-w-2xl mb-8">
              {t('home.tagline')}
            </p>

            {isAuthenticated && user && (
              <div className="mb-8 bg-theme-graphite border border-white/[0.07] max-w-md p-4">
                <p className="font-semibold mb-1 text-theme-silver">Welcome back, {user.name}!</p>
                <p className="text-sm text-theme-silver-light">
                  <span className="num text-theme-bronze">{user.entries}</span> total entries ·{' '}
                  {isSubscriber ? 'Premium Member' : 'Standard Member'}
                </p>
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-3 mb-10">
              <Link
                to="/giveaways"
                className="btn-primary text-base px-8 py-4 flex items-center justify-center space-x-2"
              >
                <Gift className="h-5 w-5" />
                <span>{t('home.viewGiveaways')}</span>
              </Link>
              {!isAuthenticated && (
                <Link
                  to="/register"
                  className="btn-secondary text-base px-8 py-4 flex items-center justify-center"
                >
                  {t('home.registerNow')}
                </Link>
              )}
            </div>

            {/* Live instrumentation — real figures, or nothing at all. */}
            {heroStats.length > 0 && (
              <div className="flex flex-wrap items-stretch gap-x-10 gap-y-4 border-t border-white/[0.10] pt-6">
                {heroStats.map(stat => (
                  <div key={stat.label}>
                    <div className="num text-2xl md:text-3xl font-medium text-theme-silver leading-none">
                      {stat.value}
                    </div>
                    <div className="mt-2 text-[11px] uppercase tracking-[0.18em] text-theme-silver-dark">
                      {stat.label}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 bg-theme-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-theme-silver mb-4">
              {t('home.whyChoose').replace('{brand}', t('brand.name'))}
            </h2>
            <p className="text-lg text-theme-silver-light max-w-2xl mx-auto">
              Every purchase automatically enters you into exclusive giveaways with amazing prizes.
            </p>
          </div>
          
          {/* Two items in a 3-column grid left a permanently empty third
              column. Matched to the actual content count. */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            <div className="tick-corners bg-theme-graphite border border-white/[0.07] p-6">
              <ShoppingBag className="h-6 w-6 text-theme-bronze mb-4" />
              <h3 className="text-lg font-semibold text-theme-silver mb-2">{t('home.shopProducts')}</h3>
              <p className="text-theme-silver-light text-sm">
                Browse our premium tactical gear and branded merchandise.
              </p>
            </div>

            <div className="tick-corners bg-theme-graphite border border-white/[0.07] p-6">
              <Star className="h-6 w-6 text-theme-bronze mb-4" />
              <h3 className="text-lg font-semibold text-theme-silver mb-2">{t('home.winPrizes')}</h3>
              <p className="text-theme-silver-light text-sm">
                Win amazing tactical gear, firearms, and training packages.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Products (temporarily hidden) */}

      {/* Active Giveaways */}
      <section className="pt-16 pb-0 bg-theme-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-theme-silver mb-4">
              {t('home.activeGiveaways')}
            </h2>
            <p className="text-lg text-theme-silver-light">
              {t('home.enterNow')}
            </p>
          </div>

          {/* Premium Benefits Row - HIDDEN ON HOME PAGE */}
          {/* <div className="mb-6">
            <div className="glass-card-dark p-4 rounded-xl border border-custom-orange/20 bg-gradient-to-r from-custom-orange/5 to-transparent">
              <h3 className="text-lg font-bold text-theme-silver mb-3 flex items-center">
                <Crown className="h-5 w-5 text-custom-orange mr-2" />
                Premium Benefits
              </h3>
              <div className="flex flex-wrap items-center gap-6">
                <div className="flex items-center space-x-2">
                  <div className="w-2 h-2 bg-custom-orange rounded-full"></div>
                  <span className="text-theme-silver-light text-sm">48-hour early access</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-2 h-2 bg-custom-orange rounded-full"></div>
                  <span className="text-theme-silver-light text-sm">1 Bronze credit monthly</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-2 h-2 bg-custom-orange rounded-full"></div>
                  <span className="text-theme-silver-light text-sm">Priority support</span>
                </div>
                <div className="ml-auto">
                  {!isAuthenticated ? (
                    <button
                      onClick={() => {
                        const product = { id: 'subscription-bronze', name: 'Bronze Subscription', price: 24.99, quantity: 1, entries: 1, tier: 'Bronze', isSubscription: true, interval: 'month' };
                        addToCart(product, 1);
                        window.location.href = '/checkout';
                      }}
                      className="bg-green-600 hover:bg-green-700 text-white font-semibold py-2 px-4 rounded-xl transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-green-500/50 text-sm"
                    >
                      Start Bronze Subscription
                    </button>
                  ) : !isSubscriber ? (
                    <button
                      onClick={() => {
                        const product = { id: 'subscription-bronze', name: 'Bronze Subscription', price: 24.99, quantity: 1, entries: 1, tier: 'Bronze', isSubscription: true, interval: 'month' };
                        addToCart(product, 1);
                        window.location.href = '/checkout';
                      }}
                      className="bg-green-600 hover:bg-green-700 text-white font-semibold py-2 px-4 rounded-xl transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-green-500/50 text-sm"
                    >
                      Upgrade to Premium
                    </button>
                  ) : (
                    <div className="text-center">
                      <div className="text-custom-orange font-semibold text-sm mb-1">Premium Active</div>
                      <div className="text-theme-silver-light text-xs">Enjoy all benefits</div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div> */}

          
          {/* A fixed 2-column grid left half the section empty whenever only
              one drawing was live, which is the common case. */}
          <div
            className={
              activeGiveaways.length === 1
                ? 'max-w-md mx-auto'
                : 'grid grid-cols-1 md:grid-cols-2 gap-6'
            }
          >
            {activeGiveaways.map((giveaway) => {
              const images = Array.isArray(giveaway.images) && giveaway.images.length > 0
                ? giveaway.images
                : [giveaway.image || '/files/Logo.png'];
              
              const endDate = giveaway?.endDate ? ensureDate(giveaway.endDate) : null;
              const endDateReached = endDate && endDate < currentTime;
              const isSoldOut = (giveaway.availableSlots === 0 || giveaway.availableSlots === '0') || endDateReached;
              const hasWinnerOrCompleted = giveaway?.status === 'completed' || !!giveaway?.winner;
              const isClosed = isSoldOut || hasWinnerOrCompleted;
              const isClassified = shouldShowClassifiedPrize(giveaway, {
                isSubscriber,
                isAdmin: Boolean(user?.isAdmin),
                isClosed,
                isAuthenticated,
                now: currentTime,
              });
              const totalSlots = Number(giveaway?.totalSlots) || 0;
              const availableSlots = Number(giveaway?.availableSlots) || 0;
              const claimedPercent = totalSlots > 0
                ? Math.min(100, Math.max(0, Math.round(((totalSlots - availableSlots) / totalSlots) * 100)))
                : 0;

              const goBronzeCheckout = () => {
                const product = { id: 'subscription-bronze', name: 'Bronze Subscription', price: 24.99, quantity: 1, entries: 1, tier: 'Bronze', isSubscription: true, interval: 'month' };
                addToCart(product, 1);
                window.location.href = '/checkout';
              };

              return (
                <div key={giveaway.id} className="tick-corners bg-theme-graphite border border-white/[0.07] hover:border-custom-orange/40 transition-colors duration-150 flex flex-col h-full">
                  <div className="relative">
                    {isClassified ? (
                      <ClassifiedPrizeMedia onUnlock={goBronzeCheckout}>
                        <CardImageGallery items={images} alt="Classified giveaway" />
                      </ClassifiedPrizeMedia>
                    ) : (
                      <CardImageGallery items={images} alt={giveaway.title} />
                    )}
                    <div className="absolute top-3 right-3">
                      <span className={`tier-badge tier-${(giveaway?.tier || 'Bronze').toLowerCase()}`}>
                        {giveaway?.tier || 'Bronze'}
                      </span>
                    </div>
                    {/* Status pills use palette tokens; the previous raw
                        green-600 / red-600 were the only saturated web colors
                        left on the page. */}
                    <div className="absolute top-3 left-3">
                      {isClosed ? (
                        <span className="inline-flex items-center gap-1.5 bg-theme-carbon/85 border border-status-closing/40 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-status-closing">
                          Sold Out
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1.5 bg-theme-carbon/85 border border-status-live/40 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-status-live">
                          <span className="h-1.5 w-1.5 rounded-full bg-status-live" aria-hidden="true" />
                          Live
                        </span>
                      )}
                    </div>
                  </div>
                  
                  <div className={`flex-1 flex flex-col px-6 pb-6 ${isClassified ? 'pt-3' : ''}`}>
                    {!isClassified && (
                      <>
                        <div className="flex items-center justify-between mb-2">
                          <h3 className="text-lg font-semibold text-theme-silver">{giveaway.title}</h3>
                        </div>
                        <p className="text-theme-silver-light mb-4 text-sm">{giveaway.description}</p>
                      </>
                    )}
                    
                    {/* Countdown Timer for Home Page - Same as Giveaways Page */}
                    {giveaway.endDate && !hasWinnerOrCompleted && (
                      <div className="mb-4">
                        <CountdownTimer 
                          endDate={isClosed ? new Date().toISOString() : giveaway.endDate}
                          currentTime={currentTime}
                          variant="default"
                          className="!bg-theme-rail"
                          isActive={!isClosed}
                        />
                      </div>
                    )}
                    
                    {/* AI CONTEXT: admin-functionality - Show "Winner Selected" message only if not hidden */}
                    {/* Reasoning: Respect hideWinnerUntil to control winner visibility, no announcement banner when hidden */}
                    {/* Journal: J-20260112-02 */}
                    {hasWinnerOrCompleted && (!giveaway.hideWinnerUntil || Date.now() >= giveaway.hideWinnerUntil) && (
                      <div className="mb-4 p-4 bg-status-live/[0.08] border border-status-live/30">
                        <div className="label-tac !text-status-live mb-1.5">Winner Selected</div>
                        {giveaway.winner?.slot && (
                          <p className="text-sm text-theme-silver-light">
                            Winning slot{' '}
                            <span className="num text-theme-silver">
                              {String(giveaway.winner.slot).padStart(2, '0')}
                            </span>
                          </p>
                        )}
                      </div>
                    )}

                    {/* Sold Out Message with Link to Giveaways Page */}
                    {isClosed && (
                      <div className="mb-4 p-4 bg-custom-orange/10 border border-custom-orange/30 rounded-lg text-center">
                        <Link
                          to={isClassified ? '/acquisition' : `/acquisition?item=${encodeURIComponent(giveaway.title || 'firearm')}`}
                          className="text-custom-orange hover:text-custom-orange/80 font-semibold underline inline-block transition-colors"
                        >
                          Request to Purchase
                        </Link>
                      </div>
                    )}
                    
                    {/*
                      Odds readout. The homepage previously carried a six-row
                      spec sheet (entries, value, early access, start, end)
                      that belongs on the detail page. What a visitor needs to
                      decide is the odds and how fast the drawing is filling —
                      and printing the odds is the thing the competition never
                      does.
                    */}
                    {!endDateReached && totalSlots > 0 && (
                      <div className="mb-4">
                        <div className="flex items-end justify-between mb-2.5">
                          <div>
                            <div className="label-tac">Odds per slot</div>
                            <div className="num text-2xl text-theme-silver leading-none mt-1.5">
                              1:{totalSlots}
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="label-tac">Slots open</div>
                            <div className="num text-2xl text-theme-silver leading-none mt-1.5">
                              {availableSlots}
                              <span className="text-theme-silver-dark text-base">/{totalSlots}</span>
                            </div>
                          </div>
                        </div>

                        <div
                          className="h-1 bg-white/[0.08] overflow-hidden"
                          role="progressbar"
                          aria-valuenow={claimedPercent}
                          aria-valuemin={0}
                          aria-valuemax={100}
                          aria-label="Giveaway fill progress"
                        >
                          <div
                            className="h-full bg-theme-bronze transition-all duration-500"
                            style={{ width: `${claimedPercent}%` }}
                          />
                        </div>
                        <div className="num mt-1.5 text-[11px] text-theme-silver-dark">
                          {claimedPercent}% claimed
                        </div>
                      </div>
                    )}

                    {!isClassified && Number(giveaway?.value) > 0 && (
                      <div className="flex items-baseline justify-between mb-4 border-t border-white/[0.07] pt-3">
                        <span className="label-tac">Prize Value</span>
                        <span className="num text-theme-silver">
                          ${Number(giveaway.value).toLocaleString()}
                        </span>
                      </div>
                    )}

                    {/*
                      The interactive slot picker lived here and made each card
                      roughly three screens tall on a landing page. Slot
                      selection is a detail-page commitment step; the card's job
                      is to sell the drawing and hand off.
                    */}

                    {/* Tier-matched Product Carousel - Only show for active (not closed/sold out) giveaways */}
                    {!isClosed && !isClassified && (() => {
                      const giveawayTier = (giveaway?.tier || 'Bronze').toLowerCase();
                      const tierProducts = (products || [])
                        .filter(p => (p?.credits > 0 || p?.entries > 0) && ((p?.tier || 'Bronze').toLowerCase() === giveawayTier))
                        .slice(0, 4);
                      if (!tierProducts || tierProducts.length === 0) return null;
                      return (
                        <div className="mb-4">
                          <h4 className="label-tac mb-2">Boost your entries</h4>
                          <div className="flex gap-3 overflow-x-auto scrollbar-none pb-1" style={{ WebkitOverflowScrolling: 'touch' }}>
                            {tierProducts.map(p => (
                              <button
                                key={p.id}
                                onClick={() => {
                                  // AI CONTEXT: payments - Preserve subscription flags when adding to cart
                                  // Journal: J-20260112-01
                                  const product = { 
                                    id: p.id, 
                                    name: p.name || p.title, 
                                    price: p.price, 
                                    salesPrice: p.salesPrice, 
                                    quantity: 1, 
                                    entries: p.credits || p.entries, 
                                    tier: p.tier || 'Bronze',
                                    isSubscription: p.isSubscription || false,
                                    interval: p.interval || 'month'
                                  };
                                  addToCart(product, 1);
                                  try { window.dispatchEvent(new Event('purchase-success')); } catch (_) {}
                                  setPrompt({ product: p });
                                }}
                                className="relative w-36 flex-shrink-0 text-left rounded-lg border border-theme-silver/20 overflow-hidden group"
                                style={{ background: 'linear-gradient(180deg, rgba(30,32,35,0.8), rgba(15,16,18,0.8))' }}
                              >
                                <div className="relative">
                                  <S3Image s3KeyOrUrl={p.image} alt={p.name || p.title} className="w-36 h-20 object-cover" fallback="/files/Logo.png" />
                                  <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" style={{
                                    background: 'linear-gradient(75deg, rgba(255,255,255,0.0) 20%, rgba(255,255,255,0.25) 40%, rgba(255,255,255,0.0) 60%)',
                                    transform: 'translateX(-30%)',
                                    animation: 'shine 1.2s ease-out both'
                                  }} />
                                  <style>{`@keyframes shine { to { transform: translateX(30%); } }`}</style>
                                </div>
                                <div className="p-2">
                                  <div className="text-[11px] text-theme-silver-light truncate">{p.name || p.title}</div>
                                  <div className="flex items-center justify-between mt-1">
                                    {p.salesPrice ? (
                                      <div className="flex items-center space-x-1">
                                        <span className="text-[10px] text-custom-orange line-through">${Number(p.price).toFixed(2)}</span>
                                        <span className="text-xs font-semibold text-theme-silver">${Number(p.salesPrice).toFixed(2)}</span>
                                      </div>
                                    ) : (
                                      <span className="text-xs font-semibold text-theme-silver">${Number(p.price).toFixed(2)}</span>
                                    )}
                                    <span className="num text-[10px] px-2 py-0.5 bg-theme-bronze text-theme-black font-semibold">+{p.credits || p.entries}</span>
                                  </div>
                                </div>
                              </button>
                            ))}
                          </div>
                        </div>
                      );
                    })()}
                    
                    <Link
                      to="/giveaways"
                      className="btn-primary w-full text-center mt-auto"
                    >
                      View Giveaway Details
                    </Link>
                  </div>
                </div>
              );
            })}
          </div>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mt-8 mb-16">
            <Link
              to="/giveaways"
              className="btn-secondary text-lg px-8 py-3 inline-flex items-center space-x-2"
            >
              <span>{t('home.viewAllGiveaways')}</span>
              <ArrowRight className="h-5 w-5" />
            </Link>
            {!isAuthenticated && (
              <Link
                to="/register"
                className="btn-primary text-lg px-8 py-3 inline-flex items-center justify-center"
              >
                {t('home.registerNow')}
              </Link>
            )}
          </div>

          {/* Post-add prompt modal */}
          {prompt && (
            <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" role="dialog" aria-modal="true">
              <div className="glass-card-dark w-full max-w-sm rounded-xl border border-theme-silver/20 p-6">
                <h3 className="text-lg font-semibold text-theme-silver mb-2">Item added to cart</h3>
                <p className="text-sm text-theme-silver-light mb-4">What would you like to do next?</p>
                <div className="flex items-center justify-end gap-2">
                  <button onClick={() => setPrompt(null)} className="px-4 py-2 rounded-lg glass-card-dark border border-theme-silver/20 text-theme-silver text-sm">Add More</button>
                  <a href={`/store?tier=Bronze`} className="px-4 py-2 rounded-lg glass-card-dark border border-theme-silver/20 text-theme-silver text-sm">Go to Store</a>
                  <a href="/checkout" className="px-4 py-2 rounded-lg bg-custom-orange text-white text-sm">Checkout</a>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* CTA Section with full-bleed American flag background */}
      <section className="relative py-0 text-white overflow-hidden min-h-[280px] bg-theme-bronze">
        <img
          src={`${process.env.PUBLIC_URL || ''}/files/american-flag.svg`}
          alt=""
          className="absolute inset-0 w-full h-full object-cover object-left-top"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/25 via-black/50 to-black/65" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            {t('home.readyToStart')}
          </h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            {t('home.joinCommunity')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/giveaways"
              className="glass-card text-white hover:bg-glass-white/20 font-semibold py-3 px-8 rounded-xl transition-all duration-300 backdrop-blur-md"
            >
              {t('home.viewGiveaways')}
            </Link>
          </div>
          <p className="mt-8 text-white/70 text-xs sm:text-sm tracking-wide uppercase">
            Proudly American
          </p>
        </div>
      </section>
    </div>
  );
};

export default Home; 